"use client";

import { Activity, ArrowUpRight, Infinity as InfinityIcon, Mountain, Stethoscope } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const stages = [{
  number: "01.",
  title: "Schema Definition",
  description: "Each submission is parsed into a structured schema: the mark scheme's criteria, and the student's step-by-step working, validated before any classification begins.",
  icon: Stethoscope,
  offsetClass: "lg:ml-0",
  to: "/ClassificationTaxonomyPage"
}, {
  number: "02.",
  title: "Deterministic Resolution",
  description: "A rule layer checks each step against the mark scheme's expected forms and a library of known error patterns, resolving clear cases for free with full interpretability.",
  icon: Activity,
  offsetClass: "lg:ml-[60px]",
  to: "/RuleLayerPage"
}, {
  number: "03.",
  title: "Escalated Classification",
  description: "Steps the rule layer can't resolve confidently are batched and passed to a prompted language-model classifier, which uses the full submission to judge intent.",
  icon: Mountain,
  offsetClass: "lg:ml-[120px]",
  to: "/ModelEscalationPage"
}, {
  number: "04.",
  title: "Continuous Fine-Tuning",
  description: "As labelled submissions accumulate, a smaller open model is fine-tuned on real data, moving the production path toward a faster, self-contained classifier.",
  icon: InfinityIcon,
  offsetClass: "lg:ml-[180px] lg:-mt-10",
  to: "/FineTuningRoadmapPage"
}];
function StageCard({
  stage,
  index
}: {
  stage: (typeof stages)[number];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15,
    rootMargin: "0px 0px -80px 0px"
  });
  const Icon = stage.icon;
  const delay = `${index * 200}ms`;
  return <div ref={ref} data-index={index} style={{
    transitionDelay: isVisible ? delay : "0ms"
  }} className={["group relative transition-all duration-[900ms] ease-out will-change-transform", stage.offsetClass, isVisible ? "opacity-100 translate-y-0 blur-0" : "opacity-0 translate-y-12 blur-sm"].join(" ")}>
      <div className="relative rounded-lg border border-border bg-card text-card-foreground p-8 sm:p-10 transition-all duration-500 ease-out hover:-translate-y-2 hover:shadow-lg hover:border-primary">
        {/* Decorative corner ticks */}
        <span aria-hidden="true" className="pointer-events-none absolute -top-px -left-px h-3.5 w-3.5 border-t-2 border-l-2 border-primary opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
        <span aria-hidden="true" className="pointer-events-none absolute -top-px -right-px h-3.5 w-3.5 border-t-2 border-r-2 border-primary opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
        <span aria-hidden="true" className="pointer-events-none absolute -bottom-px -left-px h-3.5 w-3.5 border-b-2 border-l-2 border-primary opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
        <span aria-hidden="true" className="pointer-events-none absolute -bottom-px -right-px h-3.5 w-3.5 border-b-2 border-r-2 border-primary opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

        <div className="flex items-start justify-between gap-6">
          <span className="font-default italic text-5xl sm:text-6xl leading-none tracking-tight" style={{
          color: "var(--chart-1)"
        }}>
            {stage.number}
          </span>
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-lg border border-border" style={{
          color: "var(--chart-2)"
        }}>
            <Icon strokeWidth={1.25} className="h-6 w-6" />
          </span>
        </div>

        <h3 className="font-default mt-8 text-2xl sm:text-[28px] leading-tight tracking-tight text-card-foreground">
          {stage.title}
        </h3>

        <p className="mt-4 text-[15px] leading-relaxed text-muted-foreground">
          {stage.description}
        </p>

        <div className="mt-8 pt-6 border-t border-border/60">
          <Link to={stage.to} className="inline-flex items-center gap-2 text-sm font-medium text-foreground opacity-0 -translate-x-1 transition-all duration-500 group-hover:opacity-100 group-hover:translate-x-0" style={{
          color: "var(--chart-2)"
        }}>
            <span>Learn more</span>
            <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </div>;
}
export default function IntegrationGuides() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="3386" id="integration-guides" className="bg-background text-foreground relative overflow-hidden py-24 sm:py-32 lg:py-40 px-6 sm:px-10 lg:px-16">
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-12">
          {/* Left Column: sticky narrative */}
          <div className="lg:col-span-5">
            <div ref={intro.ref} className={["lg:sticky lg:top-28 transition-all duration-[900ms] ease-out", intro.isVisible ? "opacity-100 translate-y-0 blur-0" : "opacity-0 translate-y-6 blur-sm"].join(" ")}>
              <span className="font-mono text-xs uppercase tracking-[0.22em]" style={{
              color: "var(--chart-4)"
            }}>
                Classification Pipeline · 02
              </span>

              <h2 className="font-default mt-8 text-5xl sm:text-6xl lg:text-[64px] leading-[1.0] tracking-tight text-foreground">
                Four stages,
                <br />
                one
                <span className="italic" style={{
                color: "var(--chart-1)"
              }}>
                  {" "}
                  classification.
                </span>
              </h2>

              <p className="mt-8 text-base sm:text-lg leading-relaxed text-muted-foreground max-w-md">
                Each student step moves through a fixed pipeline before it is labelled: from structured input, through deterministic checks, to model escalation only when needed.
              </p>

              <div className="mt-10 hidden lg:flex items-center gap-4">
                <Button asChild variant="outline" size="lg" className="rounded-lg border-border bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground">
                  <Link to="/WhyRessetiPage">
                    Explore the framework
                    <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                  </Link>
                </Button>
              </div>

              <div className="mt-12 hidden lg:block h-px w-24" style={{
              backgroundColor: "var(--border)"
            }} />
            </div>
          </div>

          {/* Right Column: staggered cards */}
          <div className="lg:col-span-7">
            <div className="flex flex-col gap-10 sm:gap-12">
              {stages.map((stage, i) => <StageCard key={stage.number} stage={stage} index={i} />)}
            </div>

            <div className="mt-12 lg:hidden">
              <Button asChild variant="outline" size="lg" className="rounded-lg border-border bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground">
                <Link to="/WhyRessetiPage">
                  Explore the framework
                  <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}